import React from 'react'
import { Group, Center, Text, Box, Paper, Space, Title, useMantineTheme } from '@mantine/core'
import { useMediumScreen } from '@/hooks'
import { Icon, IconName } from '@/components'

type TaskStatus = 'complete' | 'pending'

interface AirdropTaskItemProps {
  status: TaskStatus
  icon: IconName
  title: string
  description: string
  action: React.ReactNode
}

const AirdropTaskItem: React.FC<AirdropTaskItemProps> = ({ status, icon, title, description, action }) => {
  const theme = useMantineTheme()

  const { isMediumScreen } = useMediumScreen()

  const taskAction: Record<TaskStatus, () => React.ReactNode> = {
    complete: () => {
      if (isMediumScreen) {
        return (
          <Text sx={(t) => ({ color: t.colors.cyan[5] })}>
            <Icon name="checkCircle" size={36} />
          </Text>
        )
      }

      return (
        <Group spacing="sm">
          <Text inline sx={(t) => ({ color: t.colors.cyan[5] })}>
            <Icon name="checkCircle" size="xl" />
          </Text>

          <Text inline sx={(t) => ({ color: t.colors.gray[7] })}>
            Task complete!
          </Text>
        </Group>
      )
    },

    pending: () => <>{action}</>
  }

  const iconBgColor: Record<TaskStatus, string | undefined> = {
    pending: theme.colors.cyan[0],
    complete: theme.colors.gray[0]
  }

  const iconColor: Record<TaskStatus, string | undefined> = {
    pending: theme.colors.cyan[5],
    complete: theme.colors.gray[5]
  }

  const descriptionTextColor: Record<TaskStatus, string | undefined> = {
    pending: theme.colors.gray[7],
    complete: theme.colors.gray[5]
  }

  const headingTextColor: Record<TaskStatus, string | undefined> = {
    pending: undefined,
    complete: theme.colors.gray[5]
  }

  return (
    <Paper
      withBorder
      px={isMediumScreen ? 'md' : 'sm'}
      py={isMediumScreen ? theme.other.airdropTask.desktopCardVerticalPadding : 'sm'}>
      <Group align="center" position="apart">
        <Group align="center" noWrap spacing="sm">
          <Center
            sx={(t) => ({
              flexShrink: 0,
              height: t.other.airdropTask.iconSize,
              width: t.other.airdropTask.iconSize,
              background: iconBgColor[status],
              borderRadius: '50%'
            })}>
            <Text sx={{ color: iconColor[status] }}>
              <Icon name={icon} size="xl" />
            </Text>
          </Center>

          <Box>
            <Title order={isMediumScreen ? 5 : 6} sx={{ color: headingTextColor[status], lineHeight: 1 }}>
              {title}
            </Title>

            <Space h={isMediumScreen ? 'xs' : theme.other.airdropTask.titleMargin} />

            <Text inline size={isMediumScreen ? 'sm' : 'xs'} sx={{ color: descriptionTextColor[status] }}>
              {description}
            </Text>
          </Box>
        </Group>

        <Box sx={isMediumScreen ? {} : { width: '100%' }}>{taskAction[status]()}</Box>
      </Group>
    </Paper>
  )
}

export { AirdropTaskItem }
